import {processGetManyFilter, processGetManyByReferenceFilter, processFilters, addSortToUrl, readJsonOrRefuse, refuseOnHttpError} from "./provider/defaults";
import {
    CreateParams,
    DataProvider,
    DeleteManyParams,
    DeleteParams,
    GetListParams,
    GetListResult,
    GetManyParams,
    GetManyReferenceParams,
    GetManyReferenceResult,
    GetManyResult,
    GetOneParams,
    GetOneResult,
    UpdateManyParams,
    UpdateManyResult,
    UpdateParams
} from "react-admin";
import { read_cookie, delete_cookie } from 'sfcookies';
import { hostUrl, UseUrlManagerCleanUrl, apiUrl } from "../components/utils/url-manager";
import { Address } from "../types/entities";
import { addUrlToUri, removeUrlFromUri } from "./provider/defaults";

function cleanParams(address: any[]): Address {
    return {
        id: address['id'],
        line_one: address['lineOne'],
        line_two: address['lineTwo'] ?? '',
        country: address['country'],
        county: address['county'] ?? '',
        postcode: address['postcode'],
        city: address['city'],
        company: removeUrlFromUri(address['companies'], '/hq_api/companies/')
    }
}

const adminAddressDataProvider: DataProvider = {
    getList: async (resource: string, params: GetListParams) : Promise<GetListResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource;
        if (params.pagination) {
            url += '?page=' + params.pagination.page + '&itemsPerPage=' + params.pagination.perPage;
        }

        url = processFilters(url, params);
        url = UseUrlManagerCleanUrl({  url: url });
        url = addSortToUrl(url, params);
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response) => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any) => {
                let responseData: { data: Address[], total: number } = {
                    data: [],
                    total: 0
                };
                if (Array.isArray(data['hydra:member'])) {
                    let addresses: any[] = data['hydra:member'];
                    let totalItems: number = data['hydra:totalItems'];

                    for(let i: number = 0; i < addresses.length; i++) {
                        let address = addresses[i];

                        responseData.data.push(cleanParams(address))
                    }
                    responseData.total = totalItems;
                }
                return responseData;
            })
            .catch( (error: Error) => {
                console.log(error);
                return Promise.reject(error)
            });
        //return Promise.reject();
    },
    // get a single record by id
    getOne: async (resource: string, params: GetOneParams) : Promise<GetOneResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource + '/' + params.id;
        url = UseUrlManagerCleanUrl({  url: url });
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response) => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any) => {
                let responseData: { data: Address|null } = {
                    data: null
                };
                if (data['id']) {
                    responseData.data = cleanParams(data)
                }
                return responseData;
            })
            .catch( (error: Error) => {
                console.log(error);
                return Promise.reject(error);
            });
    },
    // get a list of records based on an array of ids
    getMany: async (resource: string, params: GetManyParams): Promise<GetManyResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource;
        url = processGetManyFilter(url, params);
        url = UseUrlManagerCleanUrl({  url: url });
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response) => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any) => {
                let responseData: { data: Address[], total: number } = {
                    data: [],
                    total: 0
                };

                if (Array.isArray(data['hydra:member'])) {
                    let addresses: any[] = data['hydra:member'];
                    let totalItems: number = data['hydra:totalItems'];

                    for(let i: number = 0; i < addresses.length; i++) {
                        let address: any[] = addresses[i];
                        responseData.data.push(cleanParams(address))
                    }
                    responseData.total = totalItems;
                }
                return responseData;
            })
            .catch( (error: Error) => {
                console.log(error);
                return Promise.reject(error)
            });
    },
    // get the records referenced to another record, e.g. comments for a post
    getManyReference: async (resource: string, params: GetManyReferenceParams) : Promise<GetManyReferenceResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource;
        if (params.pagination) {
            url += '?page=' + params.pagination.page + '&itemsPerPage=' + params.pagination.perPage;
        }
        url = processGetManyByReferenceFilter(url, params);
        url = UseUrlManagerCleanUrl({  url: url });
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response) => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any) => {
                let responseData: { data: Address[], total: number } = {
                    data: [],
                    total: 0
                };

                if (Array.isArray(data['hydra:member'])) {
                    let addresses: any[] = data['hydra:member'];
                    let totalItems: number = data['hydra:totalItems'];

                    for(let i: number = 0; i < addresses.length; i++) {
                        let address: any[] = addresses[i];
                        responseData.data.push(cleanParams(address));
                    }
                    responseData.total = totalItems;
                }
                return responseData;
            })
            .catch( (error: Error) => {
                console.log(error);
                return Promise.reject(error)
            });
    },
    // create a record
    create: async (resource: string, params: CreateParams) : Promise<any> => {
        let token: string = read_cookie('auth');
        params.data.lineOne = params.data.line_one;
        if (typeof params.data.line_two !== "undefined") {
            params.data.lineTwo = params.data.line_two;
        }
        params.data.company = '/hq_api/companies/' + params.data.company;
        let url: string = apiUrl + resource;
        url = UseUrlManagerCleanUrl({  url: url });
        const request: Request = new Request(url, {
            method: 'POST',
            body: JSON.stringify(params['data']),
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response) => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any) => {
                let responseData: { data: Address|null } = {
                    data: null
                };
                if (data['id']) {
                    responseData.data = cleanParams(data)
                }
                return responseData;
            })
            .catch( (error: Error) => {
                console.log(error);
                return Promise.reject(error);
            });

    },
    // update a record based on a patch
    update: async (resource: string, params: UpdateParams) : Promise<any> => {
        let token: string = read_cookie('auth');
        params.data.lineOne = params.data.line_one;
        if (typeof params.data.line_two !== "undefined") {
            params.data.lineTwo = params.data.line_two;
        }
        params.data.company = '/hq_api/companies/' + params.data.company;

        params.data.company = addUrlToUri(params.data.company, '/hq_api/companies/');
        // params.data.addresses = addUrlToUri(params.data.addresses, '/api/sales_order_addresses/');
        // params.data.boldOrderHistories = addUrlToUri(params.data.boldOrderHistories, '/api/bold_order_histories/');
        // params.data.shippingMethod = restoreEntityString(params.data.shippingMethod, '/api/sales_shipping_methods/');
        // params.data.shipping_method = restoreEntityString(params.data.shipping_method, '/api/sales_shipping_methods/');
        let url: string = apiUrl + resource + '/' + params.data.id;
        url = UseUrlManagerCleanUrl({  url: url });
        const request: Request = new Request(url, {
            method: 'PATCH',
            body: JSON.stringify(params['data']),
            headers: new Headers({
                'Content-Type' : 'application/merge-patch+json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response) => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any) => {
                let responseData: { data: Address|null } = {
                    data: null
                };
                if (data['id']) {
                    responseData.data = cleanParams(data)
                }
                return responseData;
            })
            .catch( (error: Error) => {
                console.log(error);
                return Promise.reject(error);
            });
    },
    // update a list of records based on an array of ids and a common patch
    updateMany: async (resource: string, params: UpdateManyParams) : Promise<UpdateManyResult> => {
        return Promise.reject();
    },
    // delete a record by id
    delete: async (resource: string, params: DeleteParams) : Promise<any> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource + '/' + params.id;
        const request: Request = new Request(url, {
            method: 'DELETE',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response) => {
                return refuseOnHttpError(response)
                    .then((): { data: any } => ({ data: params }));
            })
            .catch( (error: Error) => {
                console.log(error);
                return Promise.reject(error);
            });
    },
    // delete a list of records based on an array of ids
    deleteMany: async (resource: string, params: DeleteManyParams) : Promise<any> => {
        return Promise.reject();
    },
    getProfileData: (params) => {
        return Promise.resolve();
    }
}


export default adminAddressDataProvider;